/**
 * Security API — IP Access Restriction (CR-5904)
 *
 * Endpoints:
 *  - GET    /core/security/my-ip/                      → caller's request IP
 *  - GET    /core/security/ip-access-policy/           → master toggle state
 *  - PATCH  /core/security/ip-access-policy/           → enable/disable
 *  - CRUD   /core/security/ip-access-rules/            → IP/CIDR allowlist
 */
import api, { type AxiosResponse } from './client';

export type IPAccessRuleType = 'single' | 'cidr';

export interface IPAccessRule {
  id: number;
  label: string;
  rule_type: IPAccessRuleType;
  ip_value: string;
  is_active: boolean;
  created_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface IPAccessPolicy {
  enabled: boolean;
  rule_count: number;
  active_rule_count: number;
  created_at: string;
  updated_at: string;
}

export interface IPAccessRuleInput {
  label: string;
  ip_value: string;
  is_active?: boolean;
}

export interface MyIPResponse {
  ip: string;
}

export const securityAPI = {
  getMyIP: (): Promise<AxiosResponse<MyIPResponse>> =>
    api.get('/core/security/my-ip/'),

  getPolicy: (): Promise<AxiosResponse<IPAccessPolicy>> =>
    api.get('/core/security/ip-access-policy/'),

  updatePolicy: (enabled: boolean): Promise<AxiosResponse<IPAccessPolicy>> =>
    api.patch('/core/security/ip-access-policy/', { enabled }),

  listRules: (): Promise<AxiosResponse<{ results: IPAccessRule[]; count: number } | IPAccessRule[]>> =>
    api.get('/core/security/ip-access-rules/'),

  createRule: (data: IPAccessRuleInput): Promise<AxiosResponse<IPAccessRule>> =>
    api.post('/core/security/ip-access-rules/', data),

  updateRule: (id: number, data: Partial<IPAccessRuleInput>): Promise<AxiosResponse<IPAccessRule>> =>
    api.patch(`/core/security/ip-access-rules/${id}/`, data),

  deleteRule: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/security/ip-access-rules/${id}/`),
};
